import { Circle, Path, Rect, Textbox, Triangle, Polyline } from 'fabric';

import {
  grey1,
  grey10,
  grey4,
  grey6,
  purple6,
} from 'approot/shared/styles/colours.style';
import { JSONObject } from 'global/graphql/scalars.type';

export enum Tool {
  Select,
  Text,
  Pencil,
  Eraser,
  Shapes,
  Cursor,
}

export enum Shape {
  Circle,
  Triangle,
  Square,
  Rectangle,
  Line,
}

export type ToolSet = {
  tool: Tool;
  shape?: Shape;
};

export type ToolScale = {
  min: number;
  max: number;
  step: number;
};

export type PencilProps = {
  colour: string;
  size: number;
};

export type EraserProps = {
  colour: string;
  size: number;
};

export type TextProps = {
  colour: string;
  size: number;
};

export type CursorProps = {
  colour: string;
  size: number;
};

export const defaultEraser = {
  colour: grey6,
  size: 24,
};

export const defaultPencil = {
  colour: grey10,
  size: 5,
};

export const defaultText = {
  colour: grey10,
  size: 16,
};

export const defaultCursor = {
  colour: grey10,
  size: 25,
};

export const MAX_ZOOM = 10;
export const MIN_ZOOM = 0.2;
export const ZOOM_STEP = 0.1;

// max size for canvas data in bytes (2MB) / (1.9MB)
export const MAX_DATA_SIZE = 2 * 1024 * 1024;
export const WARNING_DATA_SIZE = 1.9 * 1024 * 1024;

export const DRAW_OBJECT_TYPE_CIRCLE = 'circle';
export const DRAW_OBJECT_TYPE_TRIANGLE = 'triangle';
export const DRAW_OBJECT_TYPE_TEXTBOX = 'textbox';
export const DRAW_OBJECT_TYPE_RECT = 'rect';
export const DRAW_OBJECT_TYPE_IMAGE = 'image';
export const DRAW_OBJECT_TYPE_PATH = 'path';
export const DRAW_OBJECT_TYPE_SQUARE = 'square';
export const DRAW_OBJECT_TYPE_POLYLINE = 'polyline';
export const DRAW_OBJECT_TYPE_LINE = 'line';

export const BASE_TEXT_STYLE = {
  borderOpacityWhenMoving: 1,
  cornerColor: grey1,
  cornerStrokeColor: grey4,
  cornerStyle: 'circle' as 'circle',
  transparentCorners: false,
  borderColor: purple6,
  originY: 'top' as const,
  originX: 'left' as const,
  hoverCursor: 'move',
  padding: 6,
};

export const BASE_LOCKED_STYLE = {
  hoverCursor: 'default',
  selectable: false,
  evented: false,
  editable: false,
  lockMovementX: true,
  lockMovementY: true,
  lockRotation: true,
  lockScalingX: true,
  lockScalingY: true,
  lockSkewingX: true,
  lockSkewingY: true,
};

export type FabricData = {
  version: string;
  objects: JSONObject[];
};

export type ActiveFabricObject =
  | Textbox
  | Path
  | Circle
  | Triangle
  | Rect
  | Polyline
  | null;

export const TEXT_PLACEHOLDER = 'Type here...';

export const SUB_MENU_PORTAL_ID = 'draw-sub-menu';
export const CURSOR_ELEMENT_ID = 'draw-cursor';
export const BRUSH_THICKNESS_ELEMENT_ID = 'brush-thickness-bubble';
export const CONTENT_ELEMENT_ID = 'draw-content';
export const INSTRUCTION_ELEMENT_ID = 'draw-instruction';

export const DEFAULT_SHAPE_SIZE = 50;
export const CURSOR_OFFSET = 16;
export const MIN_SHAPE_SIZE = 10;

export const OBJECT_SOURCE_KEY = 'objectSourceKey';
export const OBJECT_SOURCE_KEY_QUESTION = 'question';

export const OBJECT_DATA_SHAPE_KEY = 'dataShape';
export const OBJECT_ARTBOARD_KEY = 'isArtboard';

export const CUSTOM_CANVAS_PROPS = [
  OBJECT_SOURCE_KEY,
  OBJECT_DATA_SHAPE_KEY,
  OBJECT_ARTBOARD_KEY,
];

export const DRAW_TOOLBAR_Z_INDEX = 10;
